import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Link2, Unlink, Power } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useAuth } from "@/context/AuthContext";
import { ResourceTable } from "@/components/data/ResourceTable";
import { RefSelect } from "@/components/data/FieldInput";
import { MotifDialog } from "@/components/data/Dialogs";
import { EventList } from "@/components/data/Network";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PageHeader } from "@/components/data/StatusBadge";
import { api, errMsg, today } from "@/lib/api";

const REV_FIELD = { name: "revendeur_id", type: "ref", ref: "revendeurs" };

function AssignDialog({ tpe, onClose, onDone }) {
  const [f, setF] = useState({ revendeur_id: null, date: today(), motif: "" });
  useEffect(() => {
    if (tpe) setF({ revendeur_id: null, date: today(), motif: "" });
  }, [tpe]);
  const submit = async () => {
    try {
      await api.post(`/tpes/${tpe.id}/affecter`, { ...f, reaffectation: !!tpe.revendeur_id });
      toast.success("Affectation enregistrée");
      onClose();
      onDone();
    } catch (e) {
      toast.error(errMsg(e));
    }
  };
  return (
    <Dialog open={!!tpe} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="tpe-assign-dialog">
        <DialogHeader>
          <DialogTitle>{tpe?.revendeur_id ? "Réaffecter" : "Affecter"} le TPE {tpe?.identifiant}</DialogTitle>
          <DialogDescription>{tpe?.revendeur_id ? `Actuellement chez ${tpe.revendeur_id_label}. ` : ""}Un TPE actif n'est affecté qu'à un seul revendeur.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label>Revendeur</Label><div className="mt-1"><RefSelect field={REV_FIELD} value={f.revendeur_id} onChange={(v) => setF({ ...f, revendeur_id: v })} testId="assign-revendeur-select" /></div></div>
          <div><Label>Date d'affectation</Label><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="assign-date-input" /></div>
          <div><Label>Motif / commentaire</Label><Textarea value={f.motif} onChange={(e) => setF({ ...f, motif: e.target.value })} data-testid="assign-motif-input" /></div>
        </div>
        <DialogFooter><Button onClick={submit} disabled={!f.revendeur_id} data-testid="assign-submit-button">Confirmer</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function TpeHistory({ tpe }) {
  const [rows, setRows] = useState([]);
  useEffect(() => {
    api.get(`/tpes/${tpe.id}/historique`).then((r) => setRows(r.data)).catch(() => {});
  }, [tpe.id]);
  return <EventList rows={rows} title="Historique des mouvements" testId="tpe-history" />;
}

export default function TpePage() {
  const { can } = useAuth();
  const [assign, setAssign] = useState(null);
  const [motifDlg, setMotifDlg] = useState(null);
  const [reload, setReload] = useState(0);
  const refresh = () => setReload((n) => n + 1);
  const editable = can("tpes", "modifier");

  const extraActions = (row) => [
    { key: "assign", label: row.revendeur_id ? "Réaffecter" : "Affecter", icon: Link2, show: editable && row.statut === "actif", onClick: setAssign },
    { key: "unassign", label: "Désaffecter", icon: Unlink, show: editable && !!row.revendeur_id,
      onClick: (r) => setMotifDlg({ row: r, kind: "unassign" }) },
    { key: "toggle", label: row.statut === "actif" ? "Désactiver" : "Réactiver", icon: Power, show: editable,
      onClick: (r) => setMotifDlg({ row: r, kind: "toggle" }) },
  ];

  const confirm = async (motif) => {
    const { row, kind } = motifDlg;
    if (kind === "unassign") await api.post(`/tpes/${row.id}/desaffecter`, { motif });
    else await api.post(`/tpes/${row.id}/statut`, { statut: row.statut === "actif" ? "inactif" : "actif", motif });
    toast.success("Opération enregistrée");
    refresh();
  };

  return (
    <div>
      <PageHeader title="Terminaux (TPE)" subtitle="Parc de terminaux KGD et LONAGUI : affectation unique par revendeur, désactivation et réactivation avec historique conservé." />
      <Tabs defaultValue="parc">
        <TabsList className="mb-4"><TabsTrigger value="parc" data-testid="tab-tpe-parc">Parc TPE</TabsTrigger><TabsTrigger value="historique" data-testid="tab-tpe-historique">Historique des mouvements</TabsTrigger></TabsList>
        <TabsContent value="parc">
          <ResourceTable res="tpes" extraActions={extraActions} reloadKey={reload} detailExtra={(row) => <TpeHistory tpe={row} />} />
        </TabsContent>
        <TabsContent value="historique"><ResourceTable res="historique_tpe" reloadKey={reload} /></TabsContent>
      </Tabs>
      <AssignDialog tpe={assign} onClose={() => setAssign(null)} onDone={refresh} />
      <MotifDialog open={!!motifDlg} onOpenChange={(o) => !o && setMotifDlg(null)}
        title={motifDlg?.kind === "unassign" ? `Désaffecter ${motifDlg?.row.identifiant}` : `${motifDlg?.row.statut === "actif" ? "Désactiver" : "Réactiver"} ${motifDlg?.row.identifiant}`}
        description={motifDlg?.kind === "toggle" && motifDlg?.row.statut === "actif" ? "La désactivation retire aussi l'affectation en cours." : ""}
        onConfirm={confirm} />
    </div>
  );
}
